import {
  createSharedModulationBuffer,
  type SharedModulationLayout,
  viewSharedModulationBuffer,
  type WorkletToMainMessage,
} from "./audio-protocol";
import {
  MAX_MODULATION_ROUTES,
  clamp01,
  type EngineModulationRoute,
  type ModulationSnapshot,
  type ModulationSource,
  type ModulationTarget,
  type SynthParameters,
  type VideoParameters,
} from "./types";

const EMPTY_SNAPSHOT: ModulationSnapshot = {
  sequence: 0,
  sampleFrame: 0,
  lfo1: 0,
  lfo2: 0,
  envelope: 0,
  audioRms: 0,
  audioPeak: 0,
  audioLow: 0,
  audioMid: 0,
  audioHigh: 0,
  notePitch: 0,
  noteVelocity: 0,
  noise: 0,
  expression: 0,
  sequencer: 0,
};

export class AudioVisualBridge {
  readonly sharedBuffer?: SharedArrayBuffer;
  readonly usesSharedBuffer: boolean;

  #layout?: SharedModulationLayout;
  #fallback: ModulationSnapshot = { ...EMPTY_SNAPSHOT };
  #lastStable: ModulationSnapshot = { ...EMPTY_SNAPSHOT };

  constructor() {
    const canShare = typeof SharedArrayBuffer !== "undefined" && globalThis.crossOriginIsolated === true;
    this.usesSharedBuffer = canShare;
    if (canShare) {
      this.sharedBuffer = createSharedModulationBuffer();
      this.#layout = viewSharedModulationBuffer(this.sharedBuffer);
    }
  }

  handleMessage(message: WorkletToMainMessage): void {
    if (message.type !== "modulation") return;
    const [lfo1, lfo2, envelope, audioRms, audioPeak, sequencer, audioLow, audioMid, audioHigh, notePitch, noteVelocity, noise, expression] = message.values;
    this.#fallback = {
      sequence: message.sequence,
      sampleFrame: message.sampleFrame,
      lfo1,
      lfo2,
      envelope,
      audioRms,
      audioPeak,
      audioLow,
      audioMid,
      audioHigh,
      notePitch,
      noteVelocity,
      noise,
      expression,
      sequencer,
    };
  }

  read(): ModulationSnapshot {
    const layout = this.#layout;
    if (!layout) return this.#fallback;

    // A seqlock prevents mixing values from adjacent render quanta.
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const before = Atomics.load(layout.sequence, 0);
      if (before & 1) continue;
      const values = Array.from(layout.values);
      const after = Atomics.load(layout.sequence, 0);
      if (before !== after || (after & 1)) continue;
      const sampleFrame = values[13] + values[14] * 0x1_0000;
      this.#lastStable = {
        sequence: after,
        lfo1: values[0] ?? 0,
        lfo2: values[1] ?? 0,
        envelope: values[2] ?? 0,
        audioRms: values[3] ?? 0,
        audioPeak: values[4] ?? 0,
        sequencer: values[5] ?? 0,
        audioLow: values[6] ?? 0,
        audioMid: values[7] ?? 0,
        audioHigh: values[8] ?? 0,
        notePitch: values[9] ?? 0,
        noteVelocity: values[10] ?? 0,
        noise: values[11] ?? 0,
        expression: values[12] ?? 0,
        sampleFrame,
      };
      return this.#lastStable;
    }
    return this.#lastStable;
  }
}

function sourceValue(snapshot: ModulationSnapshot, source: ModulationSource): number {
  switch (source) {
    case "lfo1": return snapshot.lfo1;
    case "lfo2": return snapshot.lfo2;
    case "envelope": return snapshot.envelope;
    case "audioRms": return snapshot.audioRms;
    case "sequencer": return snapshot.sequencer;
    case "audioLow": return snapshot.audioLow;
    case "audioMid": return snapshot.audioMid;
    case "audioHigh": return snapshot.audioHigh;
    case "expression": return snapshot.expression;
    case "noise": return snapshot.noise;
    case "notePitch": return snapshot.notePitch;
    case "noteVelocity": return snapshot.noteVelocity;
  }
}

type TargetSpec<T> = { key: keyof T; min: number; max: number };

const VIDEO_TARGETS: Partial<Record<ModulationTarget, TargetSpec<VideoParameters>>> = {
  "video.horizontalJitter": { key: "lineShift", min: 0, max: 1 },
  "video.verticalRoll": { key: "verticalRoll", min: -1, max: 1 },
  "video.chromaPhase": { key: "chromaPhase", min: -1, max: 1 },
  "video.chromaGain": { key: "chromaGain", min: 0, max: 2 },
  "video.noise": { key: "snow", min: 0, max: 1 },
  "video.feedback": { key: "feedback", min: 0, max: 0.96 },
  "video.burstAmplitude": { key: "burstGain", min: 0, max: 1.5 },
  "video.linePosition": { key: "linePosition", min: 0, max: 1 },
  "video.lineWidth": { key: "lineWidth", min: 0, max: 0.35 },
  "video.gain": { key: "gain", min: 0, max: 2 },
  "video.offset": { key: "dcOffset", min: -1, max: 1 },
  "video.syncOffset": { key: "syncDistortion", min: -1, max: 1 },
  "video.clipping": { key: "clipping", min: 0, max: 1 },
  "video.solarize": { key: "solarize", min: 0, max: 1 },
};

const SYNTH_TARGETS: Partial<Record<ModulationTarget, TargetSpec<SynthParameters>>> = {
  "synth.osc1Detune": { key: "osc1Detune", min: -100, max: 100 },
  "synth.osc1Level": { key: "osc1Level", min: 0, max: 1 },
  "synth.osc2Detune": { key: "osc2Detune", min: -100, max: 100 },
  "synth.osc2Level": { key: "osc2Level", min: 0, max: 1 },
  "synth.noiseLevel": { key: "noise", min: 0, max: 1 },
  "synth.cutoff": { key: "filterCutoff", min: 0, max: 1 },
  "synth.resonance": { key: "filterResonance", min: 0, max: 0.98 },
  "synth.attack": { key: "attack", min: 0.002, max: 4 },
  "synth.decay": { key: "decay", min: 0.005, max: 4 },
  "synth.sustain": { key: "sustain", min: 0, max: 1 },
  "synth.release": { key: "release", min: 0.005, max: 8 },
  "synth.lfoRate": { key: "lfo1Rate", min: 0.02, max: 30 },
  "synth.drive": { key: "drive", min: 0, max: 1 },
};

/** Evaluates the append-only route list without mutating base patch values. */
export class ModulationMatrix {
  #routes: EngineModulationRoute[] = [];
  #smoothed = new Map<string, number>();

  setRoutes(routes: readonly EngineModulationRoute[]): void {
    if (routes.length > MAX_MODULATION_ROUTES) {
      throw new RangeError(`At most ${MAX_MODULATION_ROUTES} modulation routes are supported`);
    }
    const ids = new Set<string>();
    this.#routes = routes.map((route) => {
      if (ids.has(route.id)) throw new Error(`Duplicate modulation route id: ${route.id}`);
      ids.add(route.id);
      return {
        ...route,
        depth: Math.max(-1, Math.min(1, Number.isFinite(route.depth) ? route.depth : 0)),
        smoothingMs: Math.max(0, Math.min(10_000, Number.isFinite(route.smoothingMs) ? route.smoothingMs : 0)),
      };
    });
    const retained = new Map<string, number>();
    for (const route of this.#routes) {
      if (this.#smoothed.has(route.id)) retained.set(route.id, this.#smoothed.get(route.id)!);
    }
    this.#smoothed = retained;
  }

  getRoutes(): readonly EngineModulationRoute[] {
    return this.#routes.map((route) => ({ ...route }));
  }

  reset(): void {
    this.#smoothed.clear();
  }

  evaluate(
    video: Readonly<VideoParameters>,
    synth: Readonly<SynthParameters>,
    snapshot: Readonly<ModulationSnapshot>,
    deltaSeconds: number,
  ): { video: VideoParameters; synth: SynthParameters } {
    const nextVideo = { ...video };
    const nextSynth = { ...synth };
    const sums = new Map<string, number>();
    const dt = Math.max(0, Math.min(0.25, deltaSeconds));

    for (const route of this.#routes) {
      if (!route.enabled) continue;
      const raw = sourceValue(snapshot, route.source);
      const previous = this.#smoothed.get(route.id) ?? raw;
      const tau = route.smoothingMs / 1000;
      const alpha = tau <= 0 ? 1 : 1 - Math.exp(-dt / tau);
      const smoothed = previous + (raw - previous) * alpha;
      this.#smoothed.set(route.id, smoothed);
      sums.set(route.target, (sums.get(route.target) ?? 0) + route.depth * smoothed);
    }

    for (const [target, sum] of sums) {
      const videoTarget = VIDEO_TARGETS[target as ModulationTarget];
      const synthTarget = SYNTH_TARGETS[target as ModulationTarget];
      if (videoTarget) {
        const { key, min, max } = videoTarget;
        const normalized = (nextVideo[key] - min) / (max - min);
        nextVideo[key] = min + clamp01(normalized + sum) * (max - min);
      } else if (synthTarget) {
        const { key, min, max } = synthTarget;
        const current = nextSynth[key];
        if (typeof current === "number") {
          (nextSynth[key] as number) = min + clamp01((current - min) / (max - min) + sum) * (max - min);
        }
      }
    }
    return { video: nextVideo, synth: nextSynth };
  }
}
